import type { FetchBaseQueryError } from "@reduxjs/toolkit/query/react";
import axios from "axios";
import { api } from "@/api/apiSlice";
import { backendBase } from "@/api/backendBase";
import type { InventoryListResponseDto } from "@/api/dtos";
import { INVENTORY_STATUSES } from "./_models/Inventory";
import type { InventoryList, InventoryStatus } from "./_models/Inventory";
import { toInventoryFromInventoryDto } from "./_transformers/toInventoryFromInventoryDto";
import { InventoryTags } from "./InventoryTags";

export interface GetInventoryListArgs {
  statuses?: InventoryStatus[];
  /** Our location ids. */
  locationIds?: string[];
  search?: string;
}

export interface GetInventoryPageArgs extends GetInventoryListArgs {
  offset: number;
  /** Rows per page (the API allows 1–2,000). */
  limit: number;
}

const toParams = (args: GetInventoryPageArgs): Record<string, string> => {
  const params: Record<string, string> = { limit: String(args.limit), offset: String(args.offset) };
  if (args.statuses && args.statuses.length > 0) params.status = args.statuses.join(",");
  if (args.locationIds && args.locationIds.length > 0) params.locationId = args.locationIds.join(",");
  if (args.search) params.search = args.search;
  return params;
};

/**
 * One page of our inventory (GET /inventory-levels), filtered server-side. Every page carries the
 * filter's total, the status counts, every location and each source's last sync.
 */
export const getInventoryListApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getInventoryList: builder.query<InventoryList, GetInventoryPageArgs>({
      queryFn: async (args) => {
        try {
          const params = toParams(args);
          const res = await axios.get<InventoryListResponseDto>(
            `${backendBase()}/inventory-levels`,
            { params, headers: { Accept: "application/json" } },
          );
          const inventory = res.data.inventory.map((x) => toInventoryFromInventoryDto(x));
          const statusCounts = res.data.statusCounts.map((x) => ({
            status: INVENTORY_STATUSES.find((status) => status === x.status) ?? null,
            count: x.count,
          }));
          const list: InventoryList = {
            inventory,
            total: res.data.total ?? args.offset + inventory.length,
            statusCounts,
            locations: res.data.locations ?? [],
            syncs: res.data.syncs,
          };
          return { data: list };
        }
        catch (e) {
          if (axios.isAxiosError(e) && e.response) {
            return {
              error: { status: e.response.status, data: e.response.data } as FetchBaseQueryError,
            };
          }
          return {
            error: { status: "FETCH_ERROR", error: String(e) } as FetchBaseQueryError,
          };
        }
      },
      providesTags: [{ type: InventoryTags.Inventory, id: "LIST" }],
    }),
  }),
});

export const getInventoryList = getInventoryListApi.endpoints.getInventoryList;
